<template>
  <q-chip
    v-if="isEnabled"
    :color="color"
    :text-color="textColor"
    :clickable="clickable"
    :dense="dense"
    :removable="removable"
    :outline="outline"
    :label="label"
    :title="title"
    :size="size"
    :style="style"
    :tabindex="tabindex"
    :model-value="modelValue"
    @click="handleClick"
    @remove="handleRemove"
    @update:model-value="handleUpdateModelValue"
    v-bind="$attrs"
  >
    <slot />
  </q-chip>
  <CuiChip
    v-else
    :label="computedLabel"
    :icon="computedIcon"
    :removable="removable"
    :selectable="clickable || modelValue !== undefined"
    :model-value="modelValue"
    :style="style"
    @click="handleClick"
    @remove="handleRemove"
    @update:modelValue="handleUpdateModelValue"
    v-bind="$attrs"
  />
</template>

<script setup lang="ts">
import { computed, useSlots } from "vue";
import { useFeatureFlags } from "@/composables/useFeatureFlags";

const { isEnabled: checkEnabled } = useFeatureFlags();
const isEnabled = checkEnabled("dtpChip");
type Props = {
  color?: string;
  textColor?: string;
  clickable?: boolean;
  dense?: boolean;
  removable?: boolean;
  outline?: boolean;
  icon?: string;
  label?: string;
  title?: string;
  size?: string;
  style?: string | object;
  tabindex?: number;
  modelValue?: any;
};

const props = withDefaults(defineProps<Props>(), {
  color: undefined,
  textColor: undefined,
  clickable: false,
  dense: false,
  removable: false,
  outline: false,
  icon: undefined,
  label: undefined,
  title: undefined,
  size: undefined,
  style: undefined,
  tabindex: undefined,
  modelValue: undefined,
});

const slots = useSlots();

const computedLabel = computed(() => {
  if (props.label !== undefined) {
    return props.label;
  }

  // Extract text from default slot if label is undefined
  if (slots.default) {
    const slotContent = slots.default();
    if (slotContent && slotContent.length > 0) {
      const firstNode = slotContent[0];
      if (typeof firstNode.children === "string") {
        return firstNode.children;
      }
    }
  }

  return undefined;
});

const computedIcon = computed(() => {
  if (props.icon === "date") {
    return "calendar_check";
  }
  return props.icon;
});

const emit = defineEmits<{
  (e: "click", event: Event): void;
  (e: "remove"): void;
  (e: "update:modelValue", value: any): void;
}>();

const handleClick = (event: Event) => {
  emit("click", event);
};

const handleRemove = () => {
  emit("remove");
};

const handleUpdateModelValue = (value: any) => {
  emit("update:modelValue", value);
};

defineOptions({ name: "dtpChip" });
</script>
